import Toastify from 'toastify-js';
import { Form, Select, PhoneInput } from '@common/vanilla_ui/general';
import { PROGRAMS } from '@common/vanilla_ui/tracking/constants';
import { bookLiveClass } from '@common/vanilla_ui/forms/bookLiveClass';
import {
  getAttribution,
  setAttribution,
} from '@common/vanilla_ui/tracking/attribution';
import { OTP_TIMEOUT } from '@common/vanilla_ui/forms/otpForm';
import ChatFormFlowTracking from './tracking';
import {
  ELE_IDS,
  ATTRIBUTION_PROGRAM_MAPPING,
  REG_FORM_ERROR_MESSAGES,
  OTP_FORM_ERROR_MESSAGES,
} from './constants';

export default class ChatFormFlow {
  constructor(el, autoInit = true) {
    this.el = el;
    if (!el) return;

    this.tracking = new ChatFormFlowTracking(el);
    if (autoInit) this.init();
  }

  init = () => {
    this._cacheState();
    this._bindDom();
    if (this._isPhoneVerified) this._initOtpForm();
    else this._initRegistrationForm();
    this.currentStep = 0;
  };

  _cacheState = () => {
    const flag = (attr) => this.el.getAttribute(attr) === 'true';
    this._isPhoneVerified = flag('data-phone-verified');
    this._isLoggedIn = flag('data-logged-in');

    const programEl = document.querySelector('div[data-program]');
    this._program = programEl?.getAttribute('data-program') || 'academy';
  };

  _bindDom = () => {
    const phoneId = 'conversation-form__phone';
    const positionId = `${ELE_IDS.FLOW_ID}-position`;

    this.typingEl = this.el.querySelector('.conversation-form__typing');
    this.resendOtpBtn = this.el.querySelector(
      `#${ELE_IDS.OTP_FORM_ID}-resend-via-msg`,
    );

    if (this.el.querySelector(`#${positionId}`)) {
      new Select(positionId, { searchable: true });
    }
    if (this.el.querySelector(`#${phoneId}`)) new PhoneInput(phoneId);
  };

  _initRegistrationForm = () => {
    const formEl = this.el.querySelector(`#${ELE_IDS.REG_FORM_ID}`);
    if (!formEl) return;

    const endPoint = this._isLoggedIn ? '/users/v2/account' : '/users/v2';
    this.registrationForm = new Form(formEl, {
      method: this._isLoggedIn ? 'PUT' : 'POST',
      endpoint: endPoint,
      validations: this._registrationValidations(),
      createPayload: (values) => ({
        user: {
          ...values,
          program: this._program,
          skip_existing_user_check: true,
        },
        type: 'marketing',
        source: 'Course page',
        account_type: 'academy',
        'cf-turnstile-response': values['cf-turnstile-response'],
      }),
    });

    this.registrationForm
      .on('initialize', () => {
        this._bindRegistrationNavigation();
        this._bindEnterListener(formEl);
      })
      .on('submit', () => {
        this.tracking.formStatusTracking('chat-form-registration-submit');
      })
      .on('submitted', this._onRegistrationSubmitted)
      .on('error', this._handleRegFormError)
      .initialize();
  };

  _registrationValidations = () => {
    const year = new Date().getFullYear();
    const minY = year - 50;
    const maxY = year + 30;

    return {
      phone_number: {
        required: true,
        type: 'mobile',
        message: 'Please enter a valid phone number',
      },
      orgyear: {
        required: true,
        message: 'Add correct grad year',
        isValid: (v) => v >= minY && v <= maxY,
      },
      email: {
        required: true,
        type: 'email',
        message: 'Please enter a valid email',
      },
      position: {
        required: true,
        message: 'Please select a position',
        isValid: (value) => value.toLowerCase() !== '',
      },
    };
  };

  _bindEnterListener = (formEl) => {
    if (!formEl) return;

    formEl?.addEventListener('keydown', (e) => {
      const { target } = e;

      if (target?.matches('input.form-input') && e.key === 'Enter') {
        e.preventDefault();
        const step = target.closest('.conversation-form__step');
        const btn = step?.querySelector('.conversation-form__next-btn');
        if (!btn || btn.classList.contains('disabled')) return;
        const { isLastStep } = btn.dataset;
        if (isLastStep === 'true') {
          btn.click();
        } else {
          this._handleNext(btn);
        }
        target.blur();
      }
    });
  };

  _bindRegistrationNavigation = () => {
    const btns = Array.from(
      this.registrationForm?.element.querySelectorAll(
        '.conversation-form__next-btn'
      ),
    );
    btns.forEach((btn) => btn.addEventListener('click',
      () => this._handleNext(btn)));
  };

  _onRegistrationSubmitted = async () => {
    this.tracking.formStatusTracking('chat-form-registration-submitted');
    this.tracking.trackBtnClick({ click_type: 'requested_otp' });
    await this._moveToNextStep(true);
    this._initOtpForm();
    this._startOtpCountdown();
    this._hideCompletedForm();
  };

  _hideCompletedForm = () => {
    const nextBtns = this.registrationForm?.element?.querySelectorAll(
      '.conversation-form__next-btn'
    );
    nextBtns.forEach((b) => b.classList.add('invisible'));
  };

  _initOtpForm = () => {
    const otpEl = this.el.querySelector(`#${ELE_IDS.OTP_FORM_ID}`);
    if (!otpEl) return;
    otpEl.classList.remove('hidden');
    this._scrollToBottom();
    otpEl.querySelector('input.form-input')?.focus({ preventScroll: true });

    const { phone_number: phone, email } = this.registrationForm?.values || {};
    const base = this._isPhoneVerified
      ? '/users/v2/'
      : '/users/v2/account/verify';

    const otpForm = new Form(otpEl, {
      method: 'POST',
      endpoint: this._isLoggedIn ? base : '/users/v2/verify',
      createPayload: (values) => ({
        user: {
          ...values,
          phone_number: phone,
          email,
          skip_existing_user_check: true,
          program: this._program,
          rcb_prams: { attributions: this._formAttribution() },
        },
        type: 'course_landing',
        source: 'Course page',
      }),
    });

    otpForm
      .on('initialize', () => {
        const phoneEl = document.getElementById(
          `${ELE_IDS.OTP_FORM_ID}-phone-number-otp`,
        );
        phoneEl.value = this.registrationForm?.values?.['phone_number'];
        this._startOtpCountdown();
      })
      .on('submit', () => this.tracking.formStatusTracking(
        'chat-form-otp-submit',
      ))
      .on('submitted', () => {
        this.tracking.formStatusTracking('chat-form-otp-submitted');
        this.tracking.trackBtnClick({ click_type: 'otp_verified' });
        this._updateProgressBar();
        this._showSuccessMessage();
      })
      .on('error', this._handleOtpFormError)
      .initialize();
  };

  _handleNext = (btn) => {
    const { inputType, isLastStep, isEditBtn } = btn.dataset;
    const valid = this.registrationForm?.areFieldsValid(inputType);

    if (isEditBtn === 'true' && valid) {
      this._toggleEdit(btn, inputType);
      return;
    }

    if (valid) {
      this.tracking.trackBtnClick({ click_type: `${inputType}_filled` });
      if (isLastStep === 'true') return;
      this._moveToNextStep();
    }
  };

  _toggleEdit = (btn, inputType) => {
    const { stepId } = btn.dataset;
    if (btn.classList.contains('icon-edit')) {
      this.tracking.trackBtnClick({ click_type: `${inputType}_edited` });
    } else {
      this.tracking.trackBtnClick({
        click_type: `${inputType}_refilled`,
        is_edit_btn: true,
      });
    }
    btn.classList.toggle('disabled');
    btn.classList.toggle('icon-edit');

    const wrapper = this.el.querySelector(
      `[data-step-id="${stepId}"] .conversation-form__input-wrapper`,
    );
    wrapper?.classList.toggle('disabled');
  };

  async _moveToNextStep(isFinal = false) {
    this._markPrevStepCompleted();
    await this._simulateTyping();
    this.currentStep += 1;
    this._updateProgressBar();

    if (!isFinal) {
      const nextStepEl = this.el.querySelector(
        `[data-step-id="${this.currentStep}"]`,
      );
      nextStepEl?.classList.remove('hidden');

      const inputField = nextStepEl?.querySelector(
        'input.form-input',
      );
      const selectField = nextStepEl?.querySelector(
        '.sr-select__input:not([name="select-ignore-country_code"])',
      );
      if (selectField) {
        selectField.focus({ preventScroll: true });
      } else if (inputField) {
        inputField.focus({ preventScroll: true });
      }
    }

    this._scrollToBottom();
  }

  _markPrevStepCompleted = () => {
    const scope = `[data-step-id="${this.currentStep}"]`;
    const prevBtn = this.el.querySelector(
      `${scope} .conversation-form__next-btn`,
    );
    const wrapper = this.el.querySelector(
      `${scope} .conversation-form__input-wrapper`,
    );

    prevBtn?.setAttribute('data-is-edit-btn', 'true');
    prevBtn?.classList.add('disabled', 'icon-edit');
    wrapper?.classList.add('disabled');
  };

  _simulateTyping = (dur = 500) => {
    if (!this.typingEl) return Promise.resolve();

    this.typingEl.classList.remove('hidden');
    this._scrollToBottom();

    return new Promise((res) => {
      setTimeout(() => {
        this.typingEl.classList.add('hidden');
        res();
      }, dur);
    });
  };

  _scrollToBottom = () => {
    const body = this.el.querySelector('.conversation-form__body');
    body?.scrollTo({ top: body.scrollHeight, behavior: 'smooth' });
  };

  _updateProgressBar = () => {
    const pb = this.el.querySelector('.conversation-form__progress-bar');
    pb?.setAttribute('value', this.currentStep);
  };

  _formAttribution = () => {
    setAttribution('rcb_course_chat_form', {
      program:
        ATTRIBUTION_PROGRAM_MAPPING[this._program] || PROGRAMS.DATA_SCIENCE,
    });
    return getAttribution();
  };

  _toastError = (err, trackKey, map, extra = () => {}) => {
    let msg = 'Something went wrong! Please try again later';
    if (err?.isFromServer) {
      msg = map[err.response.status] || err.responseJson?.message || msg;
      if (err.response.status === 406) extra();
    }
    this.tracking.formStatusTracking(trackKey, { error_message: msg });
    Toastify({ text: msg, className: 'toastify-danger' }).showToast();
  };

  _handleRegFormError = (error) => this._toastError(
    error,
    'chat-form-registration-error',
    REG_FORM_ERROR_MESSAGES,
    () => {
      window.fallbackVisibleTurnstile(ELE_IDS.REG_FORM_ID);
      window.fallbackToRecaptchaV2(ELE_IDS.REG_FORM_ID);
    },
  );

  _handleOtpFormError = (error) => this._toastError(
    error, 'chat-form-otp-error', OTP_FORM_ERROR_MESSAGES,
  );

  _startOtpCountdown = () => {
    if (!this.resendOtpBtn) return;

    const el = this.resendOtpBtn;
    const disableCls = 'disable-pointer-events';
    clearInterval(this.otpTicker);
    let time = OTP_TIMEOUT;

    const tick = () => {
      time -= 1;
      if (!time) {
        clearInterval(this.otpTicker);
        el.textContent = 'Resend OTP via Text';
        el.classList.remove(disableCls);
        time = OTP_TIMEOUT;
      } else {
        el.textContent = `Resend OTP in ${time}s`;
        el.classList.add(disableCls);
      }
    };

    this.otpTicker = setInterval(tick, 1000);
  };

  _showSuccessMessage = () => {
    const msgEl = this.el.querySelector('#conversation-success-message');
    if (!msgEl) return;

    msgEl.classList.remove('hidden');
    this._scrollToBottom();
    setTimeout(this._runSuccessAnimation, 500);
  };

  _runSuccessAnimation = () => {
    const steps = Array.from(
      this.el.querySelectorAll('.conversation-form__success-step'),
    ) || [];
    steps.forEach((s, idx) => setTimeout(() => s.classList.add(
      'completed',
    ), (idx + 1) * 800));

    const totalDur = (steps.length + 1) * 800;
    setTimeout(() => bookLiveClass(this._program), totalDur);
  };
}
